import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { format, isToday } from "date-fns";
import { ClipboardList, Edit, User, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import {
  useDayOffRequests,
  useStaffAvailabilityRange,
} from "@/hooks/useStaffSchedule";
import {
  DayOffRequest,
  DayOffRequestStatus,
  RecurrenceType,
  ScheduleWeekDay,
  StaffAvailabilityDay,
  StaffAvailabilityRange,
  StaffSchedule,
} from "@/types/staff-schedule";
import { Team } from "@/services/teamService";

// --- Helpers ---

const JS_DAY_TO_WEEK_DAY: Record<number, ScheduleWeekDay> = {
  0: ScheduleWeekDay.Sun,
  1: ScheduleWeekDay.Mon,
  2: ScheduleWeekDay.Tue,
  3: ScheduleWeekDay.Wed,
  4: ScheduleWeekDay.Thu,
  5: ScheduleWeekDay.Fri,
  6: ScheduleWeekDay.Sat,
};

function parseDateOnly(value: string): Date {
  const [datePart] = value.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  return new Date(year, month - 1, day);
}

function toDayTimestamp(date: Date): number {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day.getTime();
}

function daysDiff(from: Date, to: Date): number {
  return Math.round((toDayTimestamp(to) - toDayTimestamp(from)) / 86_400_000);
}

function weeksDiff(from: Date, to: Date): number {
  return Math.floor(daysDiff(from, to) / 7);
}

function buildDatesFromRange(start: string, end: string): Date[] {
  const dates: Date[] = [];
  const current = new Date(start);
  const last = new Date(end);
  while (current <= last) {
    dates.push(new Date(current));
    current.setDate(current.getDate() + 1);
  }
  return dates;
}

function isScheduleOnDate(schedule: StaffSchedule, date: Date): boolean {
  if (!schedule.isActive) return false;

  const dateDay = toDayTimestamp(date);
  const fromDay = toDayTimestamp(parseDateOnly(schedule.effectiveFrom));

  if (dateDay < fromDay) return false;

  if (schedule.effectiveTo) {
    const toDay = toDayTimestamp(parseDateOnly(schedule.effectiveTo));
    if (dateDay > toDay) return false;
  }

  switch (schedule.recurrenceType) {
    case RecurrenceType.EVERY_N_DAYS: {
      const interval = schedule.recurrenceInterval ?? 1;
      const diff = daysDiff(parseDateOnly(schedule.anchorDate), date);
      return diff >= 0 && diff % interval === 0;
    }

    case RecurrenceType.WEEKLY:
      return schedule.days.includes(JS_DAY_TO_WEEK_DAY[date.getDay()]);

    case RecurrenceType.BIWEEKLY: {
      const weekDay = JS_DAY_TO_WEEK_DAY[date.getDay()];
      if (!schedule.days.includes(weekDay)) return false;
      const weeks = weeksDiff(parseDateOnly(schedule.anchorDate), date);
      return weeks >= 0 && weeks % 2 === 0;
    }

    default:
      return false;
  }
}

// --- Props ---

interface ScheduleGridProps {
  hotelId: string;
  selectedStaffIds: string[];
  selectedTeamId: string | undefined;
  startDate: string;
  endDate: string;
  schedules: StaffSchedule[];
  teams: Team[];
  staffNames: Record<string, string>;
  onScheduleClick: (schedule: StaffSchedule) => void;
  onDayOffRequestClick: (request: DayOffRequest) => void;
  onTeamChange: (teamId: string | undefined) => void;
}

// --- Main component ---

export const ScheduleGrid: React.FC<ScheduleGridProps> = ({
  hotelId,
  selectedStaffIds,
  selectedTeamId,
  startDate,
  endDate,
  schedules,
  teams,
  staffNames,
  onScheduleClick,
  onDayOffRequestClick,
  onTeamChange,
}) => {
  const { t } = useTranslation();

  const dates = useMemo(
    () => (startDate && endDate ? buildDatesFromRange(startDate, endDate) : []),
    [startDate, endDate],
  );

  const { data: rangeData, isLoading } = useStaffAvailabilityRange(
    hotelId,
    startDate,
    endDate,
  );
  const { data: dayOffRequests } = useDayOffRequests(
    hotelId,
    DayOffRequestStatus.PENDING,
  );

  const filteredData = useMemo(() => {
    if (!rangeData) return null;
    let result = rangeData;
    if (selectedStaffIds.length > 0) {
      result = result.filter((s) => selectedStaffIds.includes(s.staffId));
    }
    if (selectedTeamId) {
      const selectedTeam = teams.find((tm) => tm.id === selectedTeamId);
      if (selectedTeam) {
        result = result.filter((s) => s.teamName === selectedTeam.name);
      }
    }
    return result;
  }, [rangeData, selectedStaffIds, selectedTeamId, teams]);

  const dayDataMap = useMemo(() => {
    const map = new Map<string, StaffAvailabilityDay>();
    if (!rangeData) return map;
    for (const staff of rangeData) {
      for (const day of staff.days) {
        map.set(`${staff.staffId}:${day.date}`, day);
      }
    }
    return map;
  }, [rangeData]);

  const getDayData = (staffId: string, date: Date) =>
    dayDataMap.get(`${staffId}:${format(date, "yyyy-MM-dd")}`) ?? null;

  const schedulesByStaff = useMemo(() => {
    const map = new Map<string, StaffSchedule[]>();
    for (const schedule of schedules) {
      const staffSchedules = map.get(schedule.staffId) ?? [];
      staffSchedules.push(schedule);
      map.set(schedule.staffId, staffSchedules);
    }
    return map;
  }, [schedules]);

  const getScheduleForDay = (staffId: string, date: Date) =>
    schedulesByStaff
      .get(staffId)
      ?.find((schedule) => isScheduleOnDate(schedule, date)) ?? null;

  const dayOffRequestMap = useMemo(() => {
    const map = new Map<string, DayOffRequest>();
    if (!dayOffRequests) return map;

    for (const request of dayOffRequests) {
      const requestDates = buildDatesFromRange(
        request.startDate,
        request.endDate,
      );
      for (const date of requestDates) {
        map.set(`${request.staffId}:${format(date, "yyyy-MM-dd")}`, request);
      }
    }

    return map;
  }, [dayOffRequests]);

  const getDayOffRequest = (staffId: string, date: Date) =>
    dayOffRequestMap.get(`${staffId}:${format(date, "yyyy-MM-dd")}`) ?? null;

  const columnStats = useMemo(() => {
    if (!filteredData)
      return new Map<string, { available: number; tasks: number }>();
    const map = new Map<string, { available: number; tasks: number }>();
    for (const date of dates) {
      const dateStr = format(date, "yyyy-MM-dd");
      let available = 0;
      let tasks = 0;
      for (const staff of filteredData) {
        const day = dayDataMap.get(`${staff.staffId}:${dateStr}`);
        if (day) {
          if (day.isAvailable) available++;
          tasks += day.tasksCount;
        }
      }
      map.set(dateStr, { available, tasks });
    }
    return map;
  }, [filteredData, dates, dayDataMap]);

  if (isLoading) return <GridSkeleton />;

  if (!filteredData || filteredData.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-8 text-center">
        {t("staffSchedule.schedule.empty")}
      </p>
    );
  }

  return (
    <div className="border rounded-lg overflow-x-auto bg-slate-50">
      <div className="inline-flex min-w-full">
        {/* Staff column — narrower on mobile */}
        <div className="sticky left-0 z-20 bg-slate-50 border-r shrink-0 w-[160px] md:w-[220px]">
          <div className="h-[48px] md:h-[56px] border-b px-2 md:px-3 flex items-center bg-white">
            <Select
              value={selectedTeamId ?? "all"}
              onValueChange={(val) =>
                onTeamChange(val === "all" ? undefined : val)
              }
            >
              <SelectTrigger className="border-0 shadow-none p-0 h-auto text-xs md:text-sm font-semibold text-foreground focus:ring-0">
                <SelectValue
                  placeholder={t("staffSchedule.schedule.allTeams")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {t("staffSchedule.schedule.allTeams")}
                </SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {filteredData.map((staff) => (
            <StaffCell
              key={staff.staffId}
              staff={staff}
              staffNames={staffNames}
              t={t}
            />
          ))}
        </div>

        {/* Day columns */}
        <div className="flex flex-1">
          {dates.map((date, i) => {
            const dateStr = format(date, "yyyy-MM-dd");
            const today = isToday(date);
            const stats = columnStats.get(dateStr);
            return (
              <div
                key={i}
                className={cn(
                  "flex-1 min-w-[120px] md:min-w-[155px] border-r last:border-r-0",
                  today && "bg-royal-glow-5",
                )}
              >
                <DayColumnHeader date={date} stats={stats} today={today} />
                {filteredData.map((staff) => {
                  const dayData = getDayData(staff.staffId, date);
                  const dayOffRequest = getDayOffRequest(staff.staffId, date);
                  const schedule = getScheduleForDay(staff.staffId, date);
                  return (
                    <div
                      key={staff.staffId}
                      className="h-[80px] md:h-[100px] border-b p-1"
                    >
                      <DayCell
                        dayData={dayData}
                        dayOffRequest={dayOffRequest}
                        schedule={schedule}
                        onScheduleClick={onScheduleClick}
                        onDayOffRequestClick={onDayOffRequestClick}
                        t={t}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// --- Staff cell ---

interface StaffCellProps {
  staff: StaffAvailabilityRange;
  staffNames: Record<string, string>;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const StaffCell: React.FC<StaffCellProps> = ({ staff, staffNames, t }) => {
  const nameFromApi = [staff.firstName, staff.lastName]
    .filter(Boolean)
    .join(" ");
  const fullName =
    nameFromApi ||
    staffNames[staff.staffId] ||
    staffNames[staff.profileId ?? ""] ||
    "—";

  return (
    <div className="h-[80px] md:h-[100px] border-b px-2 md:px-3 py-2 md:py-2.5 flex items-start gap-2 md:gap-2.5 bg-white">
      <div className="relative size-8 md:size-9 rounded-full overflow-visible shrink-0 bg-muted flex items-center justify-center">
        <PresignedImageWithPlaceholder
          photo={staff.photoId ?? undefined}
          placeholder={
            <User className="size-3.5 md:size-4 text-muted-foreground" />
          }
          className="size-8 md:size-9 rounded-full object-cover"
        />
        {staff.isOnline ? (
          <span
            className="absolute right-0 top-0 size-2.5 rounded-full border border-white bg-green-300"
            title={t("staffSchedule.schedule.online")}
            aria-label={t("staffSchedule.schedule.online")}
            role="status"
          />
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-xs md:text-sm font-medium truncate">
          {fullName}
        </span>
        {(staff.position || staff.teamName) && (
          <span className="text-xs text-muted-foreground truncate hidden md:block">
            {[staff.position, staff.teamName].filter(Boolean).join(" · ")}
          </span>
        )}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <ClipboardList className="size-3" />
          <span>
            {staff.totalTasks === 1
              ? t("staffSchedule.schedule.task", { count: 1 })
              : t("staffSchedule.schedule.tasks", { count: staff.totalTasks })}
          </span>
        </div>
      </div>
    </div>
  );
};

// --- Day column header ---

interface DayColumnHeaderProps {
  date: Date;
  stats: { available: number; tasks: number } | undefined;
  today: boolean;
}

const DayColumnHeader: React.FC<DayColumnHeaderProps> = ({
  date,
  stats,
  today,
}) => (
  <div
    className={cn(
      "h-[48px] md:h-[56px] border-b px-2 md:px-2.5 py-1 md:py-1.5 flex flex-col justify-center bg-white",
      today && "bg-royal-glow-5",
    )}
  >
    <div className="flex items-baseline gap-1">
      {/* Short day name on mobile, full on md+ */}
      <span
        className={cn(
          "text-xs font-semibold",
          today ? "text-royal-glow-3" : "text-muted-foreground",
        )}
      >
        <span className="md:hidden">{format(date, "EEE")}</span>
        <span className="hidden md:inline">{format(date, "EEEE")}</span>
      </span>
      <span
        className={cn(
          "text-xs",
          today ? "text-royal-glow-3 font-medium" : "text-muted-foreground",
        )}
      >
        {format(date, "dd.MM")}
      </span>
    </div>
    {stats && (
      <div className="flex items-center gap-2 mt-0.5">
        <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
          <Users className="size-3" aria-hidden="true" />
          <span>{stats.available}</span>
        </div>
        <div className="flex items-center gap-0.5 text-xs text-muted-foreground">
          <ClipboardList className="size-3" aria-hidden="true" />
          <span>{stats.tasks}</span>
        </div>
      </div>
    )}
  </div>
);

// --- Day cell ---

interface DayCellProps {
  dayData: StaffAvailabilityDay | null;
  dayOffRequest: DayOffRequest | null;
  schedule: StaffSchedule | null;
  onScheduleClick: (schedule: StaffSchedule) => void;
  onDayOffRequestClick: (request: DayOffRequest) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

function getTasksLabel(
  tasksCount: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (tasksCount === 1) {
    return t("staffSchedule.schedule.task", { count: 1 });
  }

  if (tasksCount > 1) {
    return t("staffSchedule.schedule.tasks", { count: tasksCount });
  }

  return t("staffSchedule.schedule.noTasks");
}

interface ShiftCellProps {
  dayData: StaffAvailabilityDay;
  schedule: StaffSchedule | null;
  onScheduleClick: (schedule: StaffSchedule) => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

const ShiftCell: React.FC<ShiftCellProps> = ({
  dayData,
  schedule,
  onScheduleClick,
  t,
}) => {
  const tasksLabel = getTasksLabel(dayData.tasksCount, t);

  return (
    <button
      type="button"
      className={cn(
        "size-full rounded-lg bg-skyline-3 px-2 py-1.5 md:px-2.5 md:py-2 flex flex-col justify-between text-left transition-colors",
        schedule
          ? "cursor-pointer hover:bg-skyline-3/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-skyline-1"
          : "cursor-default",
      )}
      onClick={() => {
        if (schedule) {
          onScheduleClick(schedule);
        }
      }}
      disabled={!schedule}
      title={schedule ? t("staffSchedule.table.edit") : undefined}
      aria-label={schedule ? t("staffSchedule.table.edit") : undefined}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-skyline-1">
          {t("staffSchedule.schedule.onShift")}
        </span>
        {schedule ? (
          <Edit className="size-3 text-skyline-1/70" aria-hidden="true" />
        ) : null}
      </div>
      <div className="flex flex-col gap-0.5">
        <span className="text-xs text-skyline-1/70 hidden md:block">
          {tasksLabel}
        </span>
        <span className="text-xs font-medium text-skyline-1">
          {dayData.startTime} - {dayData.endTime}
        </span>
      </div>
    </button>
  );
};

const DayCell: React.FC<DayCellProps> = ({
  dayData,
  dayOffRequest,
  schedule,
  onScheduleClick,
  onDayOffRequestClick,
  t,
}) => {
  if (!dayData) return null;

  if (dayData.hasDayOff) {
    return (
      <div className="size-full rounded-lg border border-base-border bg-white px-2 py-1.5 md:px-2.5 md:py-2 flex flex-col justify-center">
        <span className="text-xs font-semibold text-foreground">
          {t("staffSchedule.schedule.dayOff")}
        </span>
      </div>
    );
  }

  if (dayData.hasDayOffRequest) {
    return (
      <button
        type="button"
        className={cn(
          "size-full rounded-lg bg-skyline-1 text-white px-2 py-1.5 md:px-2.5 md:py-2 flex flex-col justify-center text-left",
          dayOffRequest ? "cursor-pointer" : "cursor-default",
        )}
        onClick={() => {
          if (dayOffRequest) {
            onDayOffRequestClick(dayOffRequest);
          }
        }}
        disabled={!dayOffRequest}
      >
        <span className="text-xs font-semibold leading-tight">
          {t("staffSchedule.schedule.dayOffRequest")}
        </span>
      </button>
    );
  }

  if (dayData.isAvailable && dayData.startTime && dayData.endTime) {
    return (
      <ShiftCell
        dayData={dayData}
        schedule={schedule}
        onScheduleClick={onScheduleClick}
        t={t}
      />
    );
  }

  return null;
};

// --- Skeleton ---

const GridSkeleton: React.FC = () => (
  <div className="border rounded-lg overflow-hidden bg-slate-50">
    <div className="flex">
      <div className="w-[160px] md:w-[220px] shrink-0 border-r bg-white">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-[80px] md:h-[100px] border-b p-2 md:p-3">
            <div className="flex gap-2">
              <div className="size-8 md:size-9 rounded-full bg-muted animate-pulse" />
              <div className="flex flex-col gap-1.5 flex-1">
                <div className="h-3.5 w-20 md:w-24 rounded bg-muted animate-pulse" />
                <div className="h-3 w-12 md:w-16 rounded bg-muted animate-pulse" />
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-1">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="flex-1 min-w-[120px] md:min-w-[155px] border-r last:border-r-0"
          >
            <div className="h-[48px] md:h-[56px] border-b p-2 bg-white">
              <div className="h-3.5 w-14 md:w-20 rounded bg-muted animate-pulse" />
            </div>
            {Array.from({ length: 4 }).map((_, j) => (
              <div key={j} className="h-[80px] md:h-[100px] border-b p-1">
                <div className="size-full rounded-lg bg-muted/50 animate-pulse" />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);
